import { useState } from "react"
import { useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import "./index.scss"

import Button from "react-bootstrap/Button"
import { Container, Title, Text, Form } from "components/Form"
import { ConfirmDialog, Popup } from "components"
import { auth, loading, message, teamStatus } from "state_management"
import store from "store"
import useT from "utils/hooks/useT"

export default function LoginScreen() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")

  // Login form fields
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [token, setToken] = useState("")
  // Info message visibility and control
  const [showInfoMessage, setShowInfoMessage] = useState(false)
  const [position, setPosition] = useState(null)
  // Token visibility and control
  const [showTokenField, setShowTokenField] = useState(!!parseInt(localStorage.getItem("tokenExpireTime"), 10))
  const [showPopup, setShowPopup] = useState(false)

  // Team picker state (when user belongs to multiple teams)
  const pendingMemberships = useSelector((state) => state.auth.pendingMemberships)
  const [pendingEmail, setPendingEmail] = useState("")
  const [pendingPassword, setPendingPassword] = useState("")

  // SPEC-057 L2 — self-service "forgot password": step 1 asks for the mail (the backend always
  // answers OK — no user enumeration), step 2 takes the emailed 6-digit code + a new password.
  // The same flow serves welcome codes (new employees pick their first password here).
  const [resetStep, setResetStep] = useState(null) // null | "email" | "code"
  const [resetEmail, setResetEmail] = useState("")
  const [resetCode, setResetCode] = useState("")
  const [resetPassword, setResetPassword] = useState("")

  const handleLogin = () => {
    dispatch(auth.login(email, password))
      .then((res) => {
        if (res && res.requires_team_selection) {
          // Capture credentials now before any re-render can affect component state
          setPendingEmail(email)
          setPendingPassword(password)
          return
        }
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        // If a the user is logged in elsewhere
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        }
        // If the user needs to verify their device with MFA
        if (e.message === "You need to verify your device with Multi-Factor Authentication") {
          handleGenerateToken()
        }
      })
  }

  const handleSelectTeam = (memberId) => {
    dispatch(auth.selectTeam(pendingEmail, pendingPassword, memberId))
      .then((res) => {
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        } else if (e.message === "You need to verify your device with Multi-Factor Authentication") {
          handleGenerateToken()
        } else {
          // Any other failure — go back to the login form
          dispatch(auth.clearPendingMemberships())
        }
      })
  }

  const handleSuccesfulLogin = (res) => {
    if (res.role !== "admin" && res.role !== "customer_admin") {
      const isOnlyLibrarian = res.isOnlyLibrarian
      const isMember = res.role === "member"
      const memberId = res.id
      dispatch(teamStatus.fetchCurrent()).then((res) => {
        const onlyLibrarian = !res.status.showAnalyses && !res.status.showProjects && res.status.showLibrarian
        // This checks if FE in browser is behind FE on server and reloads
        // Page if it is. Otherwise we fetch all as usual
        const oldVersion = JSON.parse(window.localStorage.getItem("plannertechVersion"))
        if (oldVersion !== res.status.plannertechVersion) {
          var localStorageEnabled = true
          try {
            window.localStorage.setItem("plannertechVersion", JSON.stringify(res.status.plannertechVersion))
          } catch {
            localStorageEnabled = false
          }
          if (localStorageEnabled) {
            document.location.reload()
          } else {
            const fetchAllPromise = dispatch(loading.fetchAll())
            dispatch(message.success(t("adhoc.welcomeBack")))
            if (isOnlyLibrarian || onlyLibrarian) {
              navigate("/librarian")
            } else if (isMember && res.options.directToMemberPlan) {
              fetchAllPromise.then(() => {
                const memberData = store.getState().members[memberId]
                navigate(memberData?.showOnPlan === true ? "/member/" + memberId : "/")
              })
            } else {
              navigate("/")
            }
          }
        } else {
          const fetchAllPromise = dispatch(loading.fetchAll())
          dispatch(message.success(t("adhoc.welcomeBack")))
          if (isOnlyLibrarian || onlyLibrarian) {
            navigate("/librarian");
          } else if (isMember && res.options.directToMemberPlan) {
            fetchAllPromise.then(() => {
              const memberData = store.getState().members[memberId]
              navigate(memberData?.showOnPlan === true ? "/member/" + memberId : "/")
            })
          } else {
            navigate("/")
          }
        }
      })
    } else if (res.role === "admin") {
      dispatch(message.success(t("adhoc.welcomeBack")))
      navigate("/locations")
    } else {
      dispatch(message.success(t("adhoc.welcomeBack")))
      navigate("/customers")
    }
  }

  const handleGenerateToken = () => {
    dispatch(auth.getMfaToken(email, password))
      .then(() => {
        setShowTokenField(true)
        setShowInfoMessage(true)
      })
      .catch((e) => {
        if ( e.message === "Too many Authentication code requests today. Your account have been blocked until tomorrow" ) {
          resetView()
        }
      })
  }

  const handleTokenVerification = () => {
    // Verify the token logic
    dispatch(auth.verifyMfaToken(email, password, token))
      .then((res) => {
        setShowTokenField(false)
        if (res && res.requires_team_selection) {
          setPendingEmail(email)
          setPendingPassword(password)
          return
        }
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        // If a the user is logged in elsewhere
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        } else if (
          e.message === "Too many wrong attempts" ||
          e.message === "Authentication code has expired" ||
          e.message === "Too many Authentication code requests today. Your account have been blocked until tomorrow"
        ) {
          resetView()
        }
      })
  }

  function handleSubmit() {
    console.log("logging in...")
    if (showTokenField) {
      handleTokenVerification()
    } else {
      handleLogin()
    }
  }

  const resetView = () => {
    // reset states
    setShowTokenField(false)
    setShowPopup(false)
    setToken("")
  }

  const closeResetFlow = () => {
    setResetStep(null)
    setResetCode("")
    setResetPassword("")
  }

  // Same policy as the member form; the backend's model validator is the law.
  const passwordRequirements = [
    { name: tc("atLeast8Characters"), pattern: /^.{8,}$/ },
    { name: tc("atLeast1LowercaseLetter"), pattern: /[a-z]/ },
    { name: tc("atLeast1UppercaseLetter"), pattern: /[A-Z]/ },
    { name: tc("atLeast1Number"), pattern: /\d/ },
  ]

  const handleRequestResetCode = () => {
    dispatch(auth.requestPasswordReset(resetEmail))
      .then(() => setResetStep("code"))
      // A failure here is infrastructure (rate limit / server) — the toast explains, stay put.
      .catch(() => {})
  }

  const handleResetSubmit = () => {
    if (resetStep === "email") {
      handleRequestResetCode()
      return
    }
    const missing = passwordRequirements.filter((r) => !r.pattern.test(resetPassword)).map((r) => r.name)
    if (missing.length > 0) {
      dispatch(message.warning(tc("missingPasswordRequirements", { requirements: missing.join(", ") })))
      return
    }
    dispatch(auth.setPassword(resetEmail, resetCode, resetPassword))
      .then(() => {
        // Back to the login form with the mail pre-filled; the success toast says the rest.
        setEmail(resetEmail)
        closeResetFlow()
      })
      // Wrong/expired code, rate limit — the message layer toasts it; stay in the flow.
      .catch(() => {})
  }

  // Forgot-password flow (SPEC-057 L2) — replaces the login form until done/cancelled.
  if (resetStep) {
    return (
      <div className="LoginScreen-main-container">
        <Form className="LoginScreen-content" onSubmit={handleResetSubmit}>
          <h1>{tc("plannertech")}</h1>
          <h4>{tc("forgotPasswordTitle")}</h4>
          <Button variant="link" className="login-back-button" onClick={closeResetFlow}>
            ← {tc("back")}
          </Button>
          <Container>
            <Title>{tc("email")}</Title>
            <Text required type="email" value={resetEmail} disabled={resetStep === "code"} onChange={setResetEmail} />
          </Container>
          {resetStep === "code" && (
            <>
              <p className="login-reset-info">{tc("passwordResetCodeSentInfo")}</p>
              <Container>
                <Text
                  type="text"
                  required
                  placeholder={tc("passwordResetCode")}
                  value={resetCode}
                  onChange={setResetCode}
                />
              </Container>
              <Container>
                <Title>{tc("newPassword")}</Title>
                <Text required type="password" value={resetPassword} onChange={setResetPassword} />
              </Container>
            </>
          )}
          <Button className="login-button" type="submit">
            {resetStep === "email" ? tc("sendResetCode") : tc("setNewPassword")}
          </Button>
          {resetStep === "code" && (
            <Button variant="link" type="button" className="login-resend-button" onClick={handleRequestResetCode}>
              {tc("resendResetCode")}
            </Button>
          )}
        </Form>
      </div>
    )
  }

  // Team picker screen shown when user belongs to multiple teams
  if (pendingMemberships) {
    return (
      <div className="LoginScreen-main-container">
        <div className="LoginScreen-content">
          <h1>{tc("plannertech")}</h1>
          <h4>{tc("selectTeam")}</h4>
          <Button
            variant="link"
            className="login-back-button"
            onClick={() => dispatch(auth.clearPendingMemberships())}
          >
            ← {tc("back")}
          </Button>
          <div className="team-selector-list">
            {pendingMemberships.map((membership) => (
              <button
                key={membership.id}
                className="team-selector-item"
                onClick={() => handleSelectTeam(membership.id)}
              >
                {membership.initials && (
                  <span className="team-selector-item__avatar">{membership.initials}</span>
                )}
                <span className="team-selector-item__text">
                  <span className="team-selector-item__name">
                    {membership.team_name ?? (membership.role === "admin" ? tc("admin") : tc("customerAdmin"))}
                  </span>
                  {membership.location_name && (
                    <span className="team-selector-item__location">{membership.location_name}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="LoginScreen-main-container">
      <Form className="LoginScreen-content" onSubmit={handleSubmit}>
        {showPopup && (
          <ConfirmDialog
            onCancel={() => setShowPopup(false)}
            onConfirm={() => {
              setShowPopup(false)
              console.log("logging in...")
              dispatch(auth.logoutOtherDevice(email, password))
                .then((res) => {
                  if (res && res.requires_team_selection) {
                    setPendingEmail(email)
                    setPendingPassword(password)
                    return
                  }
                  handleSuccesfulLogin(res)
                })
                .catch((e) => {
                  if (e.message === "You need to verify your device with Multi-Factor Authentication") {
                    setShowTokenField(true)
                  }
                })
            }}
          >
            <h4>{tc("userAlreadyLoggedInTakeOver")}</h4>
          </ConfirmDialog>
        )}
        <h1>{tc("plannertech")}</h1>
        <Container>
          <Title>{tc("email")}</Title>
          <Text
            required
            type="email"
            value={email}
            onChange={(str) => {
              setEmail(str)
              if (showTokenField) {
                resetView()
              }
            }}
          />
        </Container>
        <Container>
          <Title>{tc("password")}</Title>
          <Text
            required
            type="password"
            value={password}
            onChange={(str) => {
              setPassword(str)
              if (showTokenField) {
                resetView()
              }
            }}
          />
        </Container>
        {showTokenField && (
          <>
            <Container>
              <Text type="text" required placeholder={tc("authenticationCode")} value={token} onChange={setToken} />
            </Container>
            {showInfoMessage && (
              <Popup
                onCancel={() => setShowInfoMessage(false)}
                singleMessage={
                  <p>{tc("authenticationCodeSentInfo")}</p>
                }
                position={position}
              />
            )}
          </>
        )}
        <Button
          className="login-button"
          type="submit"
          onClick={(event) => {
            if (!showTokenField) {
              const buttonRect = event.target.getBoundingClientRect()
              setPosition({ top: buttonRect.top })
            }
          }}
        >
          {tc("login")}
        </Button>
        <Button
          variant="link"
          type="button"
          className="login-forgot-button"
          onClick={() => {
            setResetEmail(email)
            setResetStep("email")
          }}
        >
          {tc("forgotPassword")}
        </Button>
      </Form>
    </div>
  )
}
